<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('productProfile.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('productProfile.pageTitle'),
            href: '/admin/product-profiles',
          },
          {
            text: $t('productProfile.serial'),
            active: true,
          },
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-md-4">
            <h4
              class="ts-uppercase ts-font-extrabold ts-text-blue-600 ts-shadow ts-p-3 ts-rounded"
            >
              {{ productName }}
            </h4>
            <div
              class="ts-border-2 ts-border-blue-300 ts-rounded ts-p-3 ts-mt-3 ts-overflow-x-scroll"
            >
              <SearchBox v-model="search" @onEnter="fetchData" />
              <div class="max-h ts-overflow-y-scroll ts-mt-3" id="serial">
                <ts-loading-banner :loading="loading">
                  <table class="ts-w-full ts-mt-3">
                    <thead>
                      <tr>
                        <th class="ts-bg-gray-200 ts-py-1 ts-px-2">
                          {{ $t('productProfile.serial') }}
                        </th>
                        <th class="ts-bg-gray-200 ts-py-1 ts-px-2 ts-w-32">
                          {{ $t('productProfile.status') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(r, index) in serialResources"
                        :key="index"
                        @dblclick.prevent="getHistory(r)"
                        class="ts-cursor-pointer hover:ts-bg-gray-300"
                      >
                        <td class="ts-py-1 ts-px-2">{{ r.serial_no }}</td>
                        <td class="ts-py-1 ts-px-2">{{ r.sn_status }}</td>
                      </tr>
                    </tbody>
                  </table>
                </ts-loading-banner>
              </div>
              <div
                class="d-flex justify-content-end ts-overflow-x-scroll ts-mt-3"
              >
                <ts-pagination
                  :class="serialResources.length > 15 ? 'ts-w-full' : ''"
                  v-model="serialPagination"
                  @navigate="fetchData"
                ></ts-pagination>
              </div>
            </div>
          </div>
          <div class="col-md-8">
            <div
              class="ts-shadow ts-p-3 ts-flex ts-justify-between ts-items-center"
            >
              <h4 class="ts-mb-0">{{ $t('productProfile.viewHistory') }}</h4>
              <div class="ts-space-x-2">
                  <ts-button
                    v-if="productId"
                    @click.prevent="onExportExcel"
                    :waiting="exporting"
                    class="btn-xs btn-primary"
                    > <i class="fa fa-file-excel"></i> {{ $t('productProfile.excel') }}</ts-button
                  >
                  <ts-button
                  @click.prevent="() => $router.push({ name: 'product-profile' })"
                  class="btn-xs btn-danger"
                  >{{ $t('productProfile.backToProfile') }}</ts-button
                >
              </div>
            </div>
            <ts-loading-banner :loading="loading_history">
              <div
                class="ts-pl-12 ts-space-y-3 ts-overflow-y-scroll"
                style="max-height: 60vh"
              >
                <h6 class="ts-uppercase ts-bg-blue-300 ts-p-2 ts-mt-3" v-if="history.length > 0" >{{ $t('productProfile.replacementHistory') }}</h6>
                <div v-for="(h, index) in history" :key="index">
                  <div
                    class="ts-border-b-2 ts-border-blue-800"
                    v-if="index > 0"
                  ></div>
                  <ReplacementSerialHistory :value="h" />
                </div>
              </div>
            </ts-loading-banner>
          </div>
        </div>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import SearchBox from './components/search-box'
import ReplacementSerialHistory from './components/replacement-serial-history.vue'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'productSerialList',
  components: {
    SearchBox,
    ReplacementSerialHistory,
  },
  data() {
    return {
      loading: false,
      exporting: false,
      loading_history: false,
      history: [],
      search: {
        text: '',
      },
    }
  },
  computed: {
    ...mapState('product/profile', ['serialResources', 'serialPagination']),
    productName() {
      if (this.serialResources.length > 0) {
        return this.serialResources[0].product.product_name_en
      }
      return null
    },
    productId(){
      if (this.serialResources.length > 0) {
        return this.serialResources[0].product.product_id
      }
      return null
    }
  },
  methods: {
    ...mapActions('product/profile', ['fetchSerialHistory', 'serialToExcel']),
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('product/profile/fetchSerial', {
          id: this.$route.params.id,
          attrubutes: {
            filter: {
              search: this.search.text,
            },
            ...attributes,
          },
        })
        .then((response) => {
          this.loading = false
        })
        .catch((error) => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    getHistory(record) {
      this.loading_history = true
      this.history = []
      this.fetchSerialHistory(record.serial_no)
        .then((response) => {
          this.history = response.data.replacements
        })
        .catch((error) => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading_history = false
        })
    },
    onExportExcel(){
      this.exporting = true
      this.serialToExcel(this.productId).then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          // this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    }
  },
  beforeRouteEnter(to, from, next){
    next(vm => {
      vm.fetchData()
    })
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('product/profile/RESET_RESOURCES_SERIAL')
    next()
  }
}
</script>


<style scoped>
.max-h {
  min-height: 50vh;
  max-height: 60vh;
}
#serial table {
  position: relative;
  border-collapse: collapse;
}
#serial table th {
  z-index: 1000;
  position: sticky;
  top: 0; /* Don't forget this, required for the stickiness */
  /* box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4); */
}
</style>